import { useEffect, useRef } from 'react';

// ── Particules réseau ────────────────────────────────────────────────────────
interface Particle {
  x: number; y: number;
  vx: number; vy: number;
  radius: number; opacity: number;
}

// ── Mini-tickers flottants (variations de cours BRVM) ────────────────────────
interface Ticker {
  symbol: string;
  pct: string;        // ex: "+2.34%"
  isUp: boolean;
  x: number; y: number;
  vx: number; vy: number;
  opacity: number;
  targetOpacity: number;
  phase: 'in' | 'hold' | 'out';
  age: number;
  maxAge: number;     // frames
  width: number;      // calculé à la création
}

// ── Config ────────────────────────────────────────────────────────────────────
const PARTICLE_COUNT    = 32;
const CONNECTION_DIST   = 110;
const MOUSE_REPEL_DIST  = 100;
const MOUSE_REPEL_FORCE = 0.014;
const BASE_SPEED        = 0.22;

const TICKER_MAX        = 7;    // max simultanément à l'écran
const TICKER_SPAWN_RATE = 70;   // frames entre chaque apparition (~1.2s @ 60fps)
const TICKER_HOLD       = 110;  // frames en pleine visibilité
const TICKER_FADE       = 25;   // frames de fondu

// Pool de symboles + valeurs BRVM réalistes
const TICKER_POOL: { symbol: string; pct: string; isUp: boolean }[] = [
  { symbol: 'SNTS',  pct: '+2.34%', isUp: true  },
  { symbol: 'ORAC',  pct: '+1.08%', isUp: true  },
  { symbol: 'SGBC',  pct: '-0.52%', isUp: false },
  { symbol: 'SGBC',  pct: '+1.43%', isUp: true  },
  { symbol: 'ETIT',  pct: '+3.21%', isUp: true  },
  { symbol: 'PALC',  pct: '-1.78%', isUp: false },
  { symbol: 'BOAB',  pct: '+0.89%', isUp: true  },
  { symbol: 'ONTBF', pct: '-2.10%', isUp: false },
  { symbol: 'ECOC',  pct: '+1.55%', isUp: true  },
  { symbol: 'SIBC',  pct: '-0.34%', isUp: false },
  { symbol: 'CBIBF', pct: '+4.12%', isUp: true  },
  { symbol: 'NTLC',  pct: '-1.20%', isUp: false },
  { symbol: 'TTLC',  pct: '+0.67%', isUp: true  },
];

/**
 * AuthBrandCanvas — Animation interactive sur le panneau gauche des pages auth.
 * - Réseau de particules (répulsion souris)
 * - Mini-tickers BRVM flottants avec flèches de variation
 * - Spotlight via CSS custom property --mx / --my
 */
const AuthBrandCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef  = useRef({ x: -9999, y: -9999 });
  const rafRef    = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const panel = canvas.parentElement as HTMLElement;

    // ── Resize ──────────────────────────────────────────────────────────
    const resize = () => {
      canvas.width  = panel.offsetWidth;
      canvas.height = panel.offsetHeight;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(panel);

    // ── Particules ───────────────────────────────────────────────────────
    const particles: Particle[] = Array.from({ length: PARTICLE_COUNT }, () => ({
      x:       Math.random() * canvas.width,
      y:       Math.random() * canvas.height,
      vx:      (Math.random() - 0.5) * BASE_SPEED * 2,
      vy:      (Math.random() - 0.5) * BASE_SPEED * 2,
      radius:  Math.random() * 1.6 + 0.6,
      opacity: Math.random() * 0.45 + 0.2,
    }));

    // ── Tickers ──────────────────────────────────────────────────────────
    const tickers: Ticker[] = [];
    let spawnTimer = 0;
    let poolIdx    = Math.floor(Math.random() * TICKER_POOL.length);

    const spawnTicker = () => {
      if (tickers.length >= TICKER_MAX) return;
      const data  = TICKER_POOL[poolIdx % TICKER_POOL.length];
      poolIdx++;
      // Estimer la largeur du badge (symbol + arrow + pct)
      const text  = `${data.symbol}  ${data.pct}`;
      ctx.font    = 'bold 11px Inter, sans-serif';
      const w     = ctx.measureText(text).width + 32; // padding
      const margin = 20;
      tickers.push({
        ...data,
        x:             margin + Math.random() * Math.max(canvas.width  - w - margin * 2, 10),
        y:             margin + Math.random() * Math.max(canvas.height - 24 - margin * 2, 10),
        vx:            (Math.random() - 0.5) * 0.55,
        vy:            (Math.random() - 0.5) * 0.38,
        opacity:       0,
        targetOpacity: data.isUp ? 0.82 : 0.78,
        phase:         'in',
        age:           0,
        maxAge:        TICKER_HOLD + TICKER_FADE * 2,
        width:         w,
      });
    };

    // ── Mouse ────────────────────────────────────────────────────────────
    const onMove = (e: MouseEvent) => {
      const rect = panel.getBoundingClientRect();
      mouseRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      panel.style.setProperty('--mx', `${mouseRef.current.x}px`);
      panel.style.setProperty('--my', `${mouseRef.current.y}px`);
    };
    const onLeave = () => {
      mouseRef.current = { x: -9999, y: -9999 };
      panel.style.setProperty('--mx', '-9999px');
      panel.style.setProperty('--my', '-9999px');
    };
    panel.addEventListener('mousemove', onMove);
    panel.addEventListener('mouseleave', onLeave);

    // ── Draw loop ─────────────────────────────────────────────────────────
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;

      // ── 1. Particules ────────────────────────────────────────────────
      for (const p of particles) {
        const dx = p.x - mx, dy = p.y - my;
        const dist = Math.hypot(dx, dy);
        if (dist < MOUSE_REPEL_DIST && dist > 0) {
          const force = (MOUSE_REPEL_DIST - dist) * MOUSE_REPEL_FORCE;
          p.vx += (dx / dist) * force;
          p.vy += (dy / dist) * force;
        }
        p.vx *= 0.98; p.vy *= 0.98;
        const speed = Math.hypot(p.vx, p.vy);
        if (speed < BASE_SPEED * 0.4) {
          p.vx += (Math.random() - 0.5) * 0.04;
          p.vy += (Math.random() - 0.5) * 0.04;
        }
        p.x += p.vx; p.y += p.vy;
        if (p.x < -10)                p.x = canvas.width  + 10;
        if (p.x > canvas.width  + 10) p.x = -10;
        if (p.y < -10)                p.y = canvas.height + 10;
        if (p.y > canvas.height + 10) p.y = -10;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${p.opacity})`;
        ctx.fill();
      }

      // ── 2. Connexions ────────────────────────────────────────────────
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const a = particles[i], b = particles[j];
          const d = Math.hypot(a.x - b.x, a.y - b.y);
          if (d < CONNECTION_DIST) {
            ctx.beginPath();
            ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
            ctx.strokeStyle = `rgba(255,255,255,${(1 - d / CONNECTION_DIST) * 0.14})`;
            ctx.lineWidth   = 0.7;
            ctx.stroke();
          }
        }
      }

      // ── 3. Tickers flottants ─────────────────────────────────────────
      spawnTimer++;
      if (spawnTimer >= TICKER_SPAWN_RATE) {
        spawnTimer = 0;
        spawnTicker();
      }

      ctx.font = 'bold 11px Inter, sans-serif';
      ctx.textBaseline = 'middle';

      for (let i = tickers.length - 1; i >= 0; i--) {
        const t = tickers[i];
        t.age++;
        t.x += t.vx; t.y += t.vy;

        // Gestion opacité
        if (t.phase === 'in') {
          t.opacity = Math.min(t.opacity + t.targetOpacity / TICKER_FADE, t.targetOpacity);
          if (t.opacity >= t.targetOpacity) t.phase = 'hold';
        } else if (t.phase === 'hold') {
          if (t.age >= TICKER_FADE + TICKER_HOLD) t.phase = 'out';
        } else {
          t.opacity = Math.max(t.opacity - t.targetOpacity / TICKER_FADE, 0);
          if (t.opacity <= 0) { tickers.splice(i, 1); continue; }
        }

        const h   = 22;
        const r   = 6;
        const x   = t.x, y = t.y;
        const w   = t.width;

        // Fond du badge
        const bgColor = t.isUp
          ? `rgba(16, 185, 129, ${t.opacity * 0.22})`   // vert
          : `rgba(239, 68, 68,  ${t.opacity * 0.22})`;  // rouge
        const borderColor = t.isUp
          ? `rgba(16, 185, 129, ${t.opacity * 0.55})`
          : `rgba(239, 68, 68,  ${t.opacity * 0.55})`;

        ctx.beginPath();
        ctx.roundRect(x, y - h / 2, w, h, r);
        ctx.fillStyle   = bgColor;
        ctx.fill();
        ctx.strokeStyle = borderColor;
        ctx.lineWidth   = 0.8;
        ctx.stroke();

        // Flèche (triangle SVG-like)
        const arrowX = x + 10;
        const arrowY = y;
        const aSize  = 5;
        ctx.beginPath();
        if (t.isUp) {
          ctx.moveTo(arrowX, arrowY - aSize / 2);
          ctx.lineTo(arrowX + aSize, arrowY + aSize / 2);
          ctx.lineTo(arrowX - aSize, arrowY + aSize / 2);
        } else {
          ctx.moveTo(arrowX, arrowY + aSize / 2);
          ctx.lineTo(arrowX + aSize, arrowY - aSize / 2);
          ctx.lineTo(arrowX - aSize, arrowY - aSize / 2);
        }
        ctx.closePath();
        ctx.fillStyle = t.isUp
          ? `rgba(52, 211, 153, ${t.opacity})`
          : `rgba(248, 113, 113, ${t.opacity})`;
        ctx.fill();

        // Symbole
        ctx.fillStyle = `rgba(255,255,255,${t.opacity * 0.9})`;
        ctx.font      = `bold 10px Inter, sans-serif`;
        ctx.fillText(t.symbol, arrowX + aSize + 5, arrowY);

        // Pourcentage
        ctx.fillStyle = t.isUp
          ? `rgba(52, 211, 153, ${t.opacity})`
          : `rgba(248, 113, 113, ${t.opacity})`;
        ctx.font      = `bold 10px Inter, sans-serif`;
        const symW    = ctx.measureText(t.symbol).width;
        ctx.fillText(t.pct, arrowX + aSize + 8 + symW, arrowY);
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    // Premier spawn immédiat pour ne pas avoir un écran vide
    spawnTicker();
    spawnTicker();
    draw();

    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      panel.removeEventListener('mousemove', onMove);
      panel.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute', inset: 0,
        width: '100%', height: '100%',
        pointerEvents: 'none', zIndex: 0,
      }}
      aria-hidden="true"
    />
  );
};

export default AuthBrandCanvas;
